/**
 * Shared helpers for rendering a message contact (insured person / company)
 * as an avatar: deterministic initials and background color.
 *
 * Kept framework-agnostic so they can be reused by message lists, the
 * conversation header chips, and anywhere a contact avatar is shown.
 */
export interface ContactLike {
  firstname?: string;
  lastname?: string;
  companyName?: string;
  email?: string;
  personId?: string;
}

const AVATAR_COLORS: readonly string[] = [
  '#55efc4', '#81ecec', '#74b9ff', '#a29bfe', '#dfe6e9',
  '#00b894', '#00cec9', '#0984e3', '#6c5ce7', '#ffeaa7',
  '#fab1a0', '#ff7675', '#fd79a8', '#fdcb6e', '#e17055'
];

/**
 * Display name: "First Last", or whichever of first/last name is present
 * (e.g. an operator reply that only carries a combined name), else company,
 * else email, else the fallback.
 */
export function getContactName(contact: ContactLike, fallback = ''): string {
  const name = [contact.firstname, contact.lastname]
    .filter(part => part && part.trim())
    .join(' ');
  if (name) {
    return name;
  }
  return contact.companyName || contact.email || fallback;
}

export function getContactInitials(contact: ContactLike): string {
  if (contact.firstname && contact.lastname) {
    return (contact.firstname[0] + contact.lastname[0]).toUpperCase();
  }
  const single = (contact.firstname || contact.lastname || '').trim();
  if (single) {
    const parts = single.split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return single.substring(0, 2).toUpperCase();
  }
  if (contact.companyName) {
    return contact.companyName.substring(0, 2).toUpperCase();
  }
  return '??';
}

export function getContactAvatarColor(contact: ContactLike): string {
  const identifier = contact.personId || contact.companyName || contact.email || 'default';
  let hash = 0;
  for (let i = 0; i < identifier.length; i++) {
    hash = identifier.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
